import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  api,
  ApiError,
  FormField,
  FormSubmission,
  CreateFormFieldInput,
  UpdateFormFieldInput,
} from "../lib/api";

const FIELD_TYPES = [
  { value: "text", label: "Text" },
  { value: "textarea", label: "Textarea" },
  { value: "select", label: "Select (Dropdown)" },
  { value: "checkbox", label: "Checkbox" },
  { value: "radio", label: "Radio Buttons" },
  { value: "date", label: "Date" },
  { value: "email", label: "Email" },
  { value: "phone", label: "Phone" },
];

const FORM_TYPES: Record<string, string> = {
  intake: "Intake",
  consent: "Consent",
  feedback: "Feedback",
  custom: "Custom",
};

interface FieldFormModalProps {
  formId: string;
  field?: FormField;
  existingFieldsCount: number;
  onClose: () => void;
  onSuccess: () => void;
}

function FieldFormModal({
  formId,
  field,
  existingFieldsCount,
  onClose,
  onSuccess,
}: FieldFormModalProps) {
  const isEdit = !!field;
  const [fieldType, setFieldType] = useState(field?.fieldType ?? "text");
  const [label, setLabel] = useState(field?.label ?? "");
  const [placeholder, setPlaceholder] = useState(field?.placeholder ?? "");
  const [isRequired, setIsRequired] = useState(field?.isRequired ?? false);
  const [optionsText, setOptionsText] = useState(
    field?.options?.join(", ") ?? ""
  );
  const [error, setError] = useState("");

  const showOptions = ["select", "radio"].includes(fieldType);

  const createMutation = useMutation({
    mutationFn: (data: CreateFormFieldInput) => api.formFields.create(data),
    onSuccess: () => {
      onSuccess();
      onClose();
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError("Failed to create field");
      }
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: UpdateFormFieldInput) =>
      api.formFields.update(field!.id, data),
    onSuccess: () => {
      onSuccess();
      onClose();
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError("Failed to update field");
      }
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!label.trim()) {
      setError("Label is required");
      return;
    }

    if (showOptions && !optionsText.trim()) {
      setError("Options are required for select/radio fields");
      return;
    }

    const options = showOptions
      ? optionsText
          .split(",")
          .map((o) => o.trim())
          .filter(Boolean)
      : null;

    if (isEdit) {
      const data: UpdateFormFieldInput = {
        fieldType,
        label: label.trim(),
        placeholder: placeholder.trim() || null,
        isRequired,
        options,
      };
      updateMutation.mutate(data);
    } else {
      const data: CreateFormFieldInput = {
        formId,
        fieldType,
        label: label.trim(),
        placeholder: placeholder.trim() || null,
        isRequired,
        options,
        sortOrder: existingFieldsCount,
      };
      createMutation.mutate(data);
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-semibold mb-4">
          {isEdit ? "Edit Field" : "Add New Field"}
        </h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="fieldType"
              className="block text-sm font-medium text-gray-700"
            >
              Field type
            </label>
            <select
              id="fieldType"
              value={fieldType}
              onChange={(e) => setFieldType(e.target.value)}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-amber-500 focus:outline-none focus:ring-amber-500"
            >
              {FIELD_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label
              htmlFor="fieldLabel"
              className="block text-sm font-medium text-gray-700"
            >
              Label
            </label>
            <input
              id="fieldLabel"
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-amber-500 focus:outline-none focus:ring-amber-500"
            />
          </div>

          <div>
            <label
              htmlFor="fieldPlaceholder"
              className="block text-sm font-medium text-gray-700"
            >
              Placeholder
            </label>
            <input
              id="fieldPlaceholder"
              type="text"
              value={placeholder}
              onChange={(e) => setPlaceholder(e.target.value)}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-amber-500 focus:outline-none focus:ring-amber-500"
            />
          </div>

          {showOptions && (
            <div>
              <label
                htmlFor="fieldOptions"
                className="block text-sm font-medium text-gray-700"
              >
                Options (comma-separated)
              </label>
              <input
                id="fieldOptions"
                type="text"
                value={optionsText}
                onChange={(e) => setOptionsText(e.target.value)}
                placeholder="Option 1, Option 2, Option 3"
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-amber-500 focus:outline-none focus:ring-amber-500"
              />
              <p className="mt-1 text-xs text-gray-500">
                Enter options separated by commas
              </p>
            </div>
          )}

          <div className="flex items-center gap-2">
            <input
              id="isRequired"
              type="checkbox"
              checked={isRequired}
              onChange={(e) => setIsRequired(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
            />
            <label htmlFor="isRequired" className="text-sm text-gray-700">
              Required field
            </label>
          </div>

          {error && (
            <div className="rounded-md bg-red-50 p-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <div className="flex gap-3 justify-end pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isPending}
              className="px-4 py-2 text-sm font-medium text-white bg-amber-600 rounded-md hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isPending
                ? "Saving..."
                : isEdit
                  ? "Save Changes"
                  : "Add Field"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

interface DeleteFieldModalProps {
  field: FormField;
  onClose: () => void;
  onConfirm: () => void;
  isPending: boolean;
  error?: string;
}

function DeleteFieldModal({
  field,
  onClose,
  onConfirm,
  isPending,
  error,
}: DeleteFieldModalProps) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-sm w-full p-6">
        <h2 className="text-xl font-semibold mb-4">Delete Field?</h2>
        <p className="text-gray-600 mb-4">
          Are you sure you want to delete the field &quot;{field.label}&quot;?
          This action cannot be undone.
        </p>
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
            {error}
          </div>
        )}
        <div className="flex gap-3 justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isPending}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isPending ? "Deleting..." : "Delete"}
          </button>
        </div>
      </div>
    </div>
  );
}

interface FieldRowProps {
  field: FormField;
  index: number;
  totalFields: number;
  onEdit: (field: FormField) => void;
  onDelete: (field: FormField) => void;
  onMoveUp: (field: FormField) => void;
  onMoveDown: (field: FormField) => void;
  isReordering: boolean;
}

function FieldRow({
  field,
  index,
  totalFields,
  onEdit,
  onDelete,
  onMoveUp,
  onMoveDown,
  isReordering,
}: FieldRowProps) {
  const typeLabel =
    FIELD_TYPES.find((t) => t.value === field.fieldType)?.label ??
    field.fieldType;

  return (
    <tr className="hover:bg-gray-50">
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        {index + 1}
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <span className="font-medium text-gray-900">{field.label}</span>
        {field.isRequired && (
          <span className="ml-2 px-2 py-0.5 text-xs bg-red-100 text-red-700 rounded">
            Required
          </span>
        )}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        {typeLabel}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        {field.options?.join(", ") || "-"}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
        <button
          onClick={() => onMoveUp(field)}
          disabled={index === 0 || isReordering}
          className="text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed mr-2"
          title="Move up"
        >
          <span aria-hidden="true">&#8593;</span>
          <span className="sr-only">Move up</span>
        </button>
        <button
          onClick={() => onMoveDown(field)}
          disabled={index === totalFields - 1 || isReordering}
          className="text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed mr-4"
          title="Move down"
        >
          <span aria-hidden="true">&#8595;</span>
          <span className="sr-only">Move down</span>
        </button>
        <button
          onClick={() => onEdit(field)}
          className="text-amber-600 hover:text-amber-700 mr-4"
        >
          Edit
        </button>
        <button
          onClick={() => onDelete(field)}
          className="text-red-600 hover:text-red-700"
        >
          Delete
        </button>
      </td>
    </tr>
  );
}

interface SubmissionDetailModalProps {
  submission: FormSubmission;
  onClose: () => void;
}

function SubmissionDetailModal({
  submission,
  onClose,
}: SubmissionDetailModalProps) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-semibold mb-4">Submission Details</h2>
        <div className="space-y-4">
          <div>
            <p className="text-sm text-gray-500">Submitted at</p>
            <p className="text-gray-900">
              {new Date(submission.submittedAt).toLocaleString()}
            </p>
          </div>
          {submission.clientId && (
            <div>
              <p className="text-sm text-gray-500">Client ID</p>
              <p className="text-gray-900">{submission.clientId}</p>
            </div>
          )}
          <div>
            <p className="text-sm text-gray-500 mb-2">Responses</p>
            <div className="bg-gray-50 rounded-lg p-4">
              <pre className="text-sm text-gray-800 whitespace-pre-wrap">
                {JSON.stringify(submission.responses, null, 2)}
              </pre>
            </div>
          </div>
        </div>
        <div className="flex justify-end mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export function FormDetailPage() {
  const { formId } = useParams<{ formId: string }>();
  const queryClient = useQueryClient();

  const [showFieldModal, setShowFieldModal] = useState(false);
  const [editingField, setEditingField] = useState<FormField | null>(null);
  const [deletingField, setDeletingField] = useState<FormField | null>(null);
  const [deleteFieldError, setDeleteFieldError] = useState("");
  const [viewingSubmission, setViewingSubmission] =
    useState<FormSubmission | null>(null);

  const {
    data: form,
    isLoading: isLoadingForm,
    error: formError,
  } = useQuery({
    queryKey: ["forms", formId],
    queryFn: () => api.forms.get(formId!),
    enabled: !!formId,
  });

  const {
    data: fieldsData,
    isLoading: isLoadingFields,
    error: fieldsError,
  } = useQuery({
    queryKey: ["formFields", formId],
    queryFn: () => api.formFields.list(formId!),
    enabled: !!formId,
  });

  const {
    data: submissionsData,
    isLoading: isLoadingSubmissions,
    error: submissionsError,
  } = useQuery({
    queryKey: ["formSubmissions", { formId }],
    queryFn: () => api.formSubmissions.list({ formId }),
    enabled: !!formId,
  });

  const fields = fieldsData?.data
    ? [...fieldsData.data].sort((a, b) => a.sortOrder - b.sortOrder)
    : undefined;
  const submissions = submissionsData?.data;

  const deleteFieldMutation = useMutation({
    mutationFn: (id: string) => api.formFields.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["formFields", formId] });
      setDeletingField(null);
      setDeleteFieldError("");
    },
    onError: (err) => {
      setDeleteFieldError(
        err instanceof ApiError ? err.message : "Failed to delete field"
      );
    },
  });

  const reorderMutation = useMutation({
    mutationFn: ({
      fieldId,
      newSortOrder,
    }: {
      fieldId: string;
      newSortOrder: number;
    }) => api.formFields.update(fieldId, { sortOrder: newSortOrder }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["formFields", formId] });
    },
  });

  const handleFieldSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ["formFields", formId] });
  };

  const handleEditField = (field: FormField) => {
    setEditingField(field);
    setShowFieldModal(true);
  };

  const handleCloseFieldModal = () => {
    setShowFieldModal(false);
    setEditingField(null);
  };

  const handleMoveUp = (field: FormField) => {
    if (!fields) return;
    const currentIndex = fields.findIndex((f: FormField) => f.id === field.id);
    if (currentIndex <= 0) return;

    const prevField = fields[currentIndex - 1];
    if (!prevField) return;

    // Swap sortOrder values
    reorderMutation.mutate({
      fieldId: field.id,
      newSortOrder: prevField.sortOrder,
    });
    reorderMutation.mutate({
      fieldId: prevField.id,
      newSortOrder: field.sortOrder,
    });
  };

  const handleMoveDown = (field: FormField) => {
    if (!fields) return;
    const currentIndex = fields.findIndex((f: FormField) => f.id === field.id);
    if (currentIndex >= fields.length - 1) return;

    const nextField = fields[currentIndex + 1];
    if (!nextField) return;

    // Swap sortOrder values
    reorderMutation.mutate({
      fieldId: field.id,
      newSortOrder: nextField.sortOrder,
    });
    reorderMutation.mutate({
      fieldId: nextField.id,
      newSortOrder: field.sortOrder,
    });
  };

  if (!formId) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700">Invalid form ID</p>
        </div>
      </div>
    );
  }

  if (isLoadingForm) {
    return (
      <div className="p-8 text-center">
        <div className="animate-pulse text-gray-500">Loading form...</div>
      </div>
    );
  }

  if (formError) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700">
            {formError instanceof ApiError
              ? formError.message
              : "Failed to load form"}
          </p>
          <Link
            to="/forms"
            className="mt-2 inline-block text-amber-600 hover:text-amber-700"
          >
            Back to forms
          </Link>
        </div>
      </div>
    );
  }

  if (!form) {
    return (
      <div className="p-8">
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <p className="text-gray-700">Form not found</p>
          <Link
            to="/forms"
            className="mt-2 inline-block text-amber-600 hover:text-amber-700"
          >
            Back to forms
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl">
      <div className="mb-6">
        <Link to="/forms" className="text-sm text-gray-500 hover:text-gray-700">
          &larr; Back to forms
        </Link>
      </div>

      {/* Form header */}
      <div className="bg-white shadow rounded-lg overflow-hidden mb-8">
        <div className="p-6">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{form.name}</h1>
              <div className="mt-2 flex items-center gap-4 text-sm">
                <span className="text-gray-500">
                  Type:{" "}
                  <span className="text-gray-900">
                    {FORM_TYPES[form.type] ?? form.type}
                  </span>
                </span>
                <span
                  className={form.isActive ? "text-green-600" : "text-gray-400"}
                >
                  {form.isActive ? "Active" : "Inactive"}
                </span>
                <span className="text-gray-500">
                  {form.isInternal ? "Internal" : "Public"}
                </span>
              </div>
              {form.description && (
                <p className="mt-3 text-gray-600">{form.description}</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Fields section */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900">
            Fields ({isLoadingFields ? "..." : fields?.length ?? 0})
          </h2>
          <button
            onClick={() => setShowFieldModal(true)}
            className="px-4 py-2 text-sm font-medium text-white bg-amber-600 rounded-md hover:bg-amber-700"
          >
            Add Field
          </button>
        </div>

        {isLoadingFields ? (
          <div className="text-center py-8">
            <div className="animate-pulse text-gray-500">Loading fields...</div>
          </div>
        ) : fieldsError ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-700">
              {fieldsError instanceof ApiError
                ? fieldsError.message
                : "Failed to load fields"}
            </p>
          </div>
        ) : fields?.length === 0 ? (
          <div className="bg-gray-50 rounded-lg p-8 text-center">
            <p className="text-gray-500">
              No fields yet. Add your first field to build the form.
            </p>
          </div>
        ) : (
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    #
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Label
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Options
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {fields?.map((field: FormField, index: number) => (
                  <FieldRow
                    key={field.id}
                    field={field}
                    index={index}
                    totalFields={fields.length}
                    onEdit={handleEditField}
                    onDelete={setDeletingField}
                    onMoveUp={handleMoveUp}
                    onMoveDown={handleMoveDown}
                    isReordering={reorderMutation.isPending}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Submissions section */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          Submissions ({isLoadingSubmissions ? "..." : submissions?.length ?? 0}
          )
        </h2>

        {isLoadingSubmissions ? (
          <div className="text-center py-8">
            <div className="animate-pulse text-gray-500">
              Loading submissions...
            </div>
          </div>
        ) : submissionsError ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-700">
              {submissionsError instanceof ApiError
                ? submissionsError.message
                : "Failed to load submissions"}
            </p>
          </div>
        ) : submissions?.length === 0 ? (
          <div className="bg-gray-50 rounded-lg p-8 text-center">
            <p className="text-gray-500">No submissions yet.</p>
          </div>
        ) : (
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Submitted At
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Client
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {submissions?.map((submission) => (
                  <tr key={submission.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {new Date(submission.submittedAt).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {submission.clientId ? (
                        <Link
                          to={`/clients/${submission.clientId}`}
                          className="text-amber-600 hover:text-amber-700"
                        >
                          View Client
                        </Link>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                      <button
                        onClick={() => setViewingSubmission(submission)}
                        className="text-amber-600 hover:text-amber-700"
                      >
                        View Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modals */}
      {showFieldModal && (
        <FieldFormModal
          formId={formId}
          field={editingField ?? undefined}
          existingFieldsCount={fields?.length ?? 0}
          onClose={handleCloseFieldModal}
          onSuccess={handleFieldSuccess}
        />
      )}

      {deletingField && (
        <DeleteFieldModal
          field={deletingField}
          onClose={() => {
            setDeletingField(null);
            setDeleteFieldError("");
          }}
          onConfirm={() => deleteFieldMutation.mutate(deletingField.id)}
          isPending={deleteFieldMutation.isPending}
          error={deleteFieldError}
        />
      )}

      {viewingSubmission && (
        <SubmissionDetailModal
          submission={viewingSubmission}
          onClose={() => setViewingSubmission(null)}
        />
      )}
    </div>
  );
}
